import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { ArrowRight, Check, Sparkles, ShieldCheck, Zap, Building2, Wrench, Banknote, ShoppingCart, MessageSquare, UserCog, BookOpen, ChevronRight, Quote, Loader2, Globe2, Stethoscope } from "lucide-react";
import Logo from "@/components/Logo";
import SEO from "@/components/SEO";
import ContactSalesModal from "./ContactSalesModal";

// ────────────────────────────────────────────────────────────────────────
//  Gr8Boost ERP — public marketing landing page (B2B SaaS, sells the ERP)
//  Tone: mostly trust + statistics, with a bold, on-brand hero + CTA band.
//  Designed for a global audience (no region-specific copy).
// ────────────────────────────────────────────────────────────────────────

export default function Landing() {
  const navigate = useNavigate();
  const [demoLoading, setDemoLoading] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const openContact = () => setContactOpen(true);
  const startDemo = async () => {
    setDemoLoading(true);
    try {
      const { data } = await api.post("/demo/create", { first_name: "Demo", company: "Demo Workspace" });
      localStorage.setItem("erpgo_session_token", data.session_token);
      // Tell rest of app it's a demo (Dashboard can show a banner)
      sessionStorage.setItem("gr8boost_demo_credentials", JSON.stringify(data.credentials));
      navigate("/dashboard", { replace: true });
      window.location.reload();  // ensure AuthContext rehydrates
    } catch (e) {
      alert(e?.response?.data?.detail || "Couldn't start the demo, please try again.");
      setDemoLoading(false);
    }
  };
  return (
    <div className="min-h-screen bg-white text-[#0A0A0A] font-body" data-testid="landing-page">
      <SEO
        title="Gr8Boost ERP — White-label Cloud ERP with AI Co-pilot | gr8booster.com"
        description="The global, white-label, multi-workspace cloud ERP with built-in AI co-pilot. 8 modules — Sales, Procurement, Inventory, Finance, HR, Engineering, CRM, IT. 7-day free trial — no credit card."
        canonical="https://gr8booster.com/"
      />
      <TopNav onTryLive={startDemo} demoLoading={demoLoading} onContact={openContact} />
      <Hero onTryLive={startDemo} demoLoading={demoLoading} />
      <TrustStrip />
      <ProblemSolution />
      <ModuleGrid />
      <CopilotShowcase />
      <CaseStudies />
      <PricingTeaser onContact={openContact} />
      <Testimonials />
      <FinalCTA onTryLive={startDemo} demoLoading={demoLoading} onContact={openContact} />
      <Footer onContact={openContact} />
      <ContactSalesModal open={contactOpen} onClose={() => setContactOpen(false)} />
    </div>
  );
}

/* ───── Top navigation ───── */
function TopNav({ onTryLive, demoLoading, onContact }) {
  return (
    <header className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-[#DEE2E6]" data-testid="landing-nav">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="landing-logo"><Logo size={30} /></Link>
        <nav className="hidden md:flex items-center gap-7 text-sm text-[#495057]">
          <a href="#modules" className="hover:text-[#0A0A0A]">Modules</a>
          <a href="#copilot" className="hover:text-[#0A0A0A]">Gr8Agentic AI</a>
          <a href="#pricing" className="hover:text-[#0A0A0A]">Pricing</a>
          <Link to="/roadmap" className="hover:text-[#0A0A0A]">Roadmap</Link>
        </nav>
        <div className="flex items-center gap-2">
          <button onClick={onContact} data-testid="nav-contact-sales" className="hidden sm:inline-flex text-sm font-medium px-3 py-2 hover:text-[#FF4F00]">Contact sales</button>
          <Link to="/login" className="hidden sm:inline-flex text-sm font-medium px-3 py-2 hover:text-[#FF4F00]" data-testid="nav-login">Sign in</Link>
          <button onClick={onTryLive} disabled={demoLoading} data-testid="nav-try-live"
            className="inline-flex items-center gap-1.5 bg-[#0A0A0A] hover:bg-[#FF4F00] disabled:opacity-60 text-white px-3.5 py-2 text-sm font-medium">
            {demoLoading ? <><Loader2 size={12} className="animate-spin" /> Loading…</> : <>Try it live<ChevronRight size={14} /></>}
          </button>
        </div>
      </div>
    </header>
  );
}

/* ───── Hero ───── */
function Hero({ onTryLive, demoLoading }) {
  return (
    <section className="relative overflow-hidden bg-[#0A0A0A] text-white">
      <div className="absolute inset-0 opacity-[0.07] [background-image:linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] [background-size:48px_48px]" />
      <div className="relative max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-32 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 text-[10px] sm:text-xs uppercase tracking-[0.25em] text-[#FF4F00] font-mono border border-[#FF4F00]/30 bg-[#FF4F00]/10 px-3 py-1.5">
            <Sparkles size={12} /> Now with embedded AI Co-pilot
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter mt-5 leading-[1.05]">
            The white-label ERP<br />that runs <span className="text-[#FF4F00]">itself.</span>
          </h1>
          <p className="text-base sm:text-lg text-white/70 mt-5 max-w-xl leading-relaxed">
            Gr8Boost ERP is the multi-workspace cloud platform built for modern businesses — anywhere in the world.
            <span className="text-white"> 8 modules, double-entry GL, AI co-pilot, PayPal + Stripe + MoMo billing</span> — ready to brand as your own.
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <button onClick={onTryLive} disabled={demoLoading} data-testid="hero-try-live"
              className="inline-flex items-center gap-2 bg-[#FF4F00] hover:bg-white hover:text-[#0A0A0A] disabled:opacity-60 text-white font-medium px-5 py-3 text-sm transition-colors">
              {demoLoading ? <><Loader2 size={14} className="animate-spin" /> Spinning up demo…</> : <>Try it live — no signup <ArrowRight size={14} /></>}
            </button>
            <a href="#demo" data-testid="hero-demo" className="inline-flex items-center gap-2 border border-white/40 hover:border-white px-5 py-3 text-sm font-medium">
              Book a guided demo
            </a>
            <Link to="/login" data-testid="hero-signin" className="text-sm text-white/70 hover:text-white underline underline-offset-4 ml-1">
              Sign in
            </Link>
          </div>
          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-xs text-white/60">
            <span className="inline-flex items-center gap-1.5"><ShieldCheck size={12} className="text-[#0B7A75]" /> SOC-2-ready architecture</span>
            <span className="inline-flex items-center gap-1.5"><Zap size={12} className="text-[#F5B700]" /> Live in 30 days</span>
            <span className="inline-flex items-center gap-1.5"><Globe2 size={12} className="text-[#0033A0]" /> Multi-currency, multi-region tax</span>
          </div>
        </div>

        {/* Mock screen mock-up */}
        <div className="lg:col-span-5 relative">
          <div className="bg-white text-[#0A0A0A] border border-white/20 shadow-2xl rounded-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#495057] font-mono">/ Dashboard · Live</div>
              <div className="flex gap-1.5"><span className="w-2 h-2 rounded-full bg-[#0B7A75]" /><span className="w-2 h-2 rounded-full bg-[#F5B700]" /><span className="w-2 h-2 rounded-full bg-[#D90429]" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              {[
                { label: "Sales · MTD", v: "$482K", c: "#0B7A75" },
                { label: "Receivables", v: "$178K", c: "#FF4F00" },
                { label: "Stock value", v: "$1.2M", c: "#0033A0" },
                { label: "Net income", v: "$96K", c: "#0B7A75" },
              ].map((k) => (
                <div key={k.label} className="border border-[#DEE2E6] p-3">
                  <div className="text-[9px] uppercase tracking-[0.2em] text-[#495057]">{k.label}</div>
                  <div className="text-lg font-bold mt-1 tabular-nums" style={{ color: k.c }}>{k.v}</div>
                </div>
              ))}
            </div>
            <div className="border border-[#DEE2E6] p-3 mt-2">
              <div className="text-[9px] uppercase tracking-[0.2em] text-[#495057] mb-2">Gr8Agentic AI · just now</div>
              <div className="text-xs leading-relaxed">
                <Sparkles size={10} className="inline text-[#FF4F00] mr-1" />
                You are <span className="font-bold">5 days from your aging cliff</span>. 3 invoices ($86K) tip into 30+ days — want me to draft reminder messages to the customers now?
              </div>
            </div>
          </div>
          <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-[#FF4F00]/20 blur-3xl rounded-full" />
        </div>
      </div>
    </section>
  );
}

/* ───── Trust strip ───── */
function TrustStrip() {
  const stats = [
    { value: "8", label: "Built-in modules" },
    { value: "30 days", label: "Average time to go live" },
    { value: "Multi-region", label: "Currencies & tax engines" },
    { value: "24/7", label: "Cloud uptime SLA" },
  ];
  // Production-grade security signals — every one is enforced in code.
  const security = [
    { icon: ShieldCheck, label: "TLS 1.3 + HSTS" },
    { icon: Zap,         label: "Rate-limited" },
    { icon: Building2,   label: "Multi-tenant isolation" },
    { icon: ShoppingCart, label: "PCI-scope minimized" },
    { icon: BookOpen,    label: "GDPR-ready" },
    { icon: Wrench,      label: "OWASP-hardened" },
  ];
  return (
    <section className="border-y border-[#DEE2E6] bg-[#F8F9FA] py-10 sm:py-12" data-testid="trust-strip">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center text-[10px] sm:text-xs uppercase tracking-[0.25em] text-[#495057] mb-7">A platform built for ambitious businesses worldwide</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 text-center">
          {stats.map((s) => (
            <div key={s.label}>
              <div className="font-heading text-3xl sm:text-4xl font-bold tracking-tighter" style={{ color: "#0A0A0A" }}>{s.value}</div>
              <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-[#495057] mt-1.5">{s.label}</div>
            </div>
          ))}
        </div>
        {/* Built-for-trust strip — security signals, no buzzwords */}
        <div className="mt-10 pt-7 border-t border-[#DEE2E6]" data-testid="built-for-trust">
          <div className="text-center text-[10px] uppercase tracking-[0.25em] text-[#0B7A75] font-mono mb-4">Built for trust</div>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-[11px] sm:text-xs text-[#495057]">
            {security.map((s) => {
              const Icon = s.icon;
              return (
                <span key={s.label} className="inline-flex items-center gap-1.5" data-testid={`trust-badge-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                  <Icon size={13} className="text-[#0B7A75]" />
                  <span className="font-medium text-[#0A0A0A]">{s.label}</span>
                </span>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───── Problem / Solution ───── */
function ProblemSolution() {
  return (
    <section className="py-16 sm:py-24" data-testid="problem-solution">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 sm:gap-14">
        <div>
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.25em] text-[#D90429] font-mono">The Problem</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tighter mt-2 leading-tight">
            Spreadsheets + legacy accounting + chat groups<br />don&apos;t scale past your second branch.
          </h2>
          <ul className="mt-6 space-y-3 text-[#495057] text-sm sm:text-base">
            {["Sales reps don't know real-time stock.", "Finance closes the books 3 weeks late.", "Field technicians scribble on paper, then lose it.", "Payroll & statutory filings are painful every month.", "Your CEO checks numbers in chat threads — at midnight."].map((p) => (
              <li key={p} className="flex items-start gap-2.5"><span className="text-[#D90429] mt-0.5">✕</span>{p}</li>
            ))}
          </ul>
        </div>
        <div>
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.25em] text-[#0B7A75] font-mono">The Gr8Boost Solution</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tighter mt-2 leading-tight">
            One platform. Every team. <br/>Real numbers in real time.
          </h2>
          <ul className="mt-6 space-y-3 text-[#495057] text-sm sm:text-base">
            {["Sales, inventory, finance, HR — one source of truth.", "Auto double-entry GL on every transaction.", "FEFO batches, cycle counts, supplier scorecards.", "Multi-currency, multi-region tax: VAT, GST, sales tax — configurable per market.", "Ask Gr8Agentic AI in plain English. Get answers in 2 seconds."].map((p) => (
              <li key={p} className="flex items-start gap-2.5"><Check size={16} className="text-[#0B7A75] mt-0.5 shrink-0" />{p}</li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

/* ───── Module Grid ───── */
function ModuleGrid() {
  const modules = [
    { icon: ShoppingCart, name: "Sales & CRM", body: "Quote → invoice → payment with credit limits, returns, aging, statements." },
    { icon: Building2, name: "Procurement", body: "PR → RFQ → PO → GRN with 3-way matching and supplier scorecards." },
    { icon: Stethoscope, name: "Inventory", body: "FEFO batch tracking, cycle counts, transfers, RTV — for cold-chain, retail & manufacturing." },
    { icon: Banknote, name: "Finance & GL", body: "Bank recon, budgets, fixed assets, double-entry journals & period close." },
    { icon: UserCog, name: "HR & Payroll", body: "Statutory tax engines, leave, attendance, onboarding, appraisals, recruitment ATS." },
    { icon: Wrench, name: "Field Service", body: "Job orders, planned maintenance, warranties, technician KPIs, photo reports." },
    { icon: MessageSquare, name: "CRM & Cases", body: "SLA-driven tickets across email, WhatsApp & calls + knowledge base & NPS." },
    { icon: ShieldCheck, name: "IT & Compliance", body: "Audit log dashboard, license tracker, quarterly access reviews, backups." },
  ];
  return (
    <section id="modules" className="bg-[#F8F9FA] border-y border-[#DEE2E6] py-16 sm:py-24" data-testid="modules-section">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.25em] text-[#FF4F00] font-mono">Modules</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tighter mt-2 leading-tight">Built for how modern businesses <span className="text-[#FF4F00]">actually</span> work.</h2>
          <p className="text-[#495057] mt-3 text-sm sm:text-base">Every line of code shipped is opinionated for the operations your team runs every day — not retrofitted from a generic global ERP.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
          {modules.map((m, i) => {
            const Icon = m.icon;
            return (
              <div key={m.name} className="border border-[#0A0A0A] bg-white p-5 hover:shadow-lg transition-shadow" data-testid={`module-${i}`}>
                <div className="w-9 h-9 border border-[#0A0A0A] grid place-items-center mb-3"><Icon size={16} className="text-[#FF4F00]" /></div>
                <div className="font-heading font-bold tracking-tight">{m.name}</div>
                <div className="text-xs text-[#495057] mt-1.5 leading-relaxed">{m.body}</div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ───── Gr8Agentic Showcase ───── */
function CopilotShowcase() {
  return (
    <section id="copilot" className="bg-[#0A0A0A] text-white py-16 sm:py-24" data-testid="copilot-section">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.25em] text-[#FF4F00] font-mono inline-flex items-center gap-2"><Sparkles size={12} /> Gr8Agentic AI</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tighter mt-2 leading-tight">
            Your senior analyst,<br />embedded in every page.
          </h2>
          <p className="text-white/70 mt-4 text-sm sm:text-base leading-relaxed">
            Ask anything in plain English. Gr8Agentic looks at your live data, runs the query, and writes a one-paragraph executive summary. It can also <em>act</em> — create invoices, raise POs, send WhatsApps, post journal entries — on your behalf.
          </p>
          <ul className="mt-5 space-y-2 text-sm">
            {["“Who's my worst-paying customer this quarter?”", "“Draft a credit-note for invoice 0042 and apply it.”", "“What's my payroll tax liability for this month?”", "“Send a Morning Brief to leadership every Mon 7am.”"].map((q) => (
              <li key={q} className="flex items-start gap-2 text-white/80"><Quote size={12} className="text-[#FF4F00] mt-1 shrink-0" />{q}</li>
            ))}
          </ul>
        </div>
        <div className="bg-white text-[#0A0A0A] p-5 sm:p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#495057] font-mono mb-3">/ Gr8Agentic Console</div>
          <div className="space-y-3 text-sm">
            <div className="bg-[#F8F9FA] border border-[#DEE2E6] px-3 py-2"><span className="text-[10px] uppercase tracking-[0.2em] text-[#495057] mr-2">You</span>Why is cash low this week?</div>
            <div className="border border-[#0A0A0A] px-3 py-2">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#FF4F00] mb-1">Gr8Agentic</div>
              Three things: (1) Northgate Hospital invoice <span className="font-mono text-xs">INV-0142</span> sits at <span className="font-mono text-xs">$86,000</span>, 28 days unpaid. (2) Operating bank float dropped <span className="font-mono text-xs">$240,000</span> vs last week — a supplier prepayment. (3) June payroll posts in 4 days (≈ <span className="font-mono text-xs">$92,000</span> net). Want me to chase Northgate via WhatsApp now?
              <div className="mt-2 flex flex-wrap gap-2 text-[10px]">
                <span className="border border-[#0A0A0A] px-2 py-0.5">📊 Run aging report</span>
                <span className="border border-[#0A0A0A] px-2 py-0.5">📲 Send reminder</span>
                <span className="bg-[#FF4F00] text-white px-2 py-0.5">✓ Yes, chase Northgate</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───── Use cases (replaces "case studies" until first real customer signs) ───── */
function CaseStudies() {
  const cases = [
    { icon: Stethoscope, tag: "Healthcare & Distribution", title: "Cold-chain visibility, end-to-end",
      body: "FEFO batch tracking, expiry alerts and serial-numbered warranty registers — built in. Field engineers post photo reports from the patient's bedside." },
    { icon: Wrench, tag: "Engineering & Field Service", title: "Planned maintenance that pays for itself",
      body: "Recurring PMs auto-spawn job orders. Warranty register flags coverage instantly. Technician KPIs reveal who's profitable and who isn't." },
    { icon: ShoppingCart, tag: "Wholesale & Retail", title: "Sell faster, collect sooner",
      body: "Quick-sale POS, credit limits, customer statements, aging reports — and one-click MoMo / card payment links sent to customers' phones." },
  ];
  return (
    <section id="usecases" className="py-16 sm:py-24" data-testid="cases-section">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.25em] text-[#FF4F00] font-mono">Use cases</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tighter mt-2 leading-tight">One platform. <span className="text-[#FF4F00]">Many</span> ways to use it.</h2>
          <p className="text-[#495057] mt-3 text-sm sm:text-base">From distribution to engineering services to retail — Gr8Boost flexes to your operating model.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-5 mt-10">
          {cases.map((c, i) => {
            const Icon = c.icon;
            return (
              <div key={c.title} className="border border-[#0A0A0A] bg-white p-6" data-testid={`usecase-${i}`}>
                <div className="w-10 h-10 border border-[#0A0A0A] grid place-items-center"><Icon size={18} className="text-[#FF4F00]" /></div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#495057] font-mono mt-4">{c.tag}</div>
                <h3 className="font-heading text-xl font-bold tracking-tight mt-1.5">{c.title}</h3>
                <p className="text-[#495057] mt-2.5 text-sm leading-relaxed">{c.body}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ───── Pricing Teaser (no public prices — sales-led) ───── */
function PricingTeaser({ onContact }) {
  const [cadence, setCadence] = useState("yearly");
  const plans = [
    { name: "Starter", desc: "For small teams getting started.", features: ["Up to 5 users", "Sales + Inventory", "Email support"], popular: false },
    { name: "Growth", desc: "Everything most companies need.", features: ["Up to 25 users", "All 8 modules", "Gr8Agentic AI", "Priority email"], popular: true },
    { name: "Scale", desc: "For larger and multi-entity operations.", features: ["Unlimited users", "White-label branding", "24/7 support", "Custom integrations"], popular: false },
  ];
  return (
    <section id="pricing" className="bg-[#F8F9FA] border-y border-[#DEE2E6] py-16 sm:py-24" data-testid="pricing-section">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.25em] text-[#FF4F00] font-mono">Pricing</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tighter mt-2">Simple, fair, scales with you.</h2>
          <p className="text-[#495057] mt-3 text-sm sm:text-base">Tell us about your business and we'll send pricing within one business day. Annual billing comes with 2 months free.</p>
        </div>

        {/* Billing cadence toggle */}
        <div className="flex items-center justify-center mt-7" data-testid="cadence-toggle">
          <div className="inline-flex border border-[#0A0A0A] bg-white">
            {[{ k: "monthly", l: "Monthly" }, { k: "yearly", l: "Yearly · Save 2 months" }].map((o) => (
              <button key={o.k} onClick={() => setCadence(o.k)} data-testid={`cadence-${o.k}`}
                className={`px-4 py-2 text-xs sm:text-sm transition-colors ${cadence === o.k ? "bg-[#0A0A0A] text-white" : "text-[#495057] hover:text-[#0A0A0A]"}`}>
                {o.l}
              </button>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-5 mt-8 max-w-4xl mx-auto">
          {plans.map((p) => (
            <div key={p.name} className={`p-6 bg-white flex flex-col ${p.popular ? "border-2 border-[#FF4F00] relative" : "border border-[#0A0A0A]"}`} data-testid={`landing-plan-${p.name.toLowerCase()}`}>
              {p.popular && <div className="absolute -top-3 left-5 bg-[#FF4F00] text-white text-[10px] uppercase tracking-[0.2em] px-2 py-0.5">Most popular</div>}
              <div className="text-xs uppercase tracking-[0.2em] text-[#495057]">{p.name}</div>
              <div className="mt-3 font-heading text-2xl sm:text-3xl font-bold tracking-tight">Custom</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#FF4F00] mt-1">Pricing on request</div>
              <p className="text-xs text-[#495057] mt-3">{p.desc}</p>
              <ul className="mt-3 space-y-1.5 flex-1">
                {p.features.map((f, j) => (<li key={j} className="flex items-start gap-1.5 text-xs"><Check size={12} className="text-[#0B7A75] mt-0.5 shrink-0" />{f}</li>))}
              </ul>
              <button onClick={onContact} data-testid={`plan-cta-${p.name.toLowerCase()}`} className={`mt-5 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-medium ${p.popular ? "bg-[#FF4F00] hover:bg-[#0A0A0A] text-white" : "border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white"}`}>
                Contact sales <ArrowRight size={14} />
              </button>
            </div>
          ))}
        </div>
        <div className="text-center text-xs text-[#495057] mt-5" data-testid="pricing-cadence-note">
          {cadence === "yearly" ? "Annual contracts include 2 months free and locked pricing for 12 months." : "Monthly contracts — cancel anytime, no penalty."}
        </div>
      </div>
    </section>
  );
}

/* ───── Testimonials ───── */
function Testimonials() {
  const quotes = [
    { quote: "Books close in days, not weeks. Bank reconciliation actually balances now and our auditors love it.", who: "Finance Director", role: "Healthcare Distribution" },
    { quote: "Our field engineers stopped losing job sheets. The planned-maintenance scheduler alone paid for the platform.", who: "Operations Manager", role: "Engineering Services" },
    { quote: "The AI co-pilot caught duplicate vendor payments before they hit the bank. It's like having an extra controller on the team.", who: "CFO", role: "Mid-market Retailer" },
  ];
  return (
    <section className="py-16 sm:py-24" data-testid="testimonials">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.25em] text-[#FF4F00] font-mono">From the teams using us</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tighter mt-2">Quiet confidence, loud results.</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5 mt-10">
          {quotes.map((q, i) => (
            <figure key={i} className="border border-[#0A0A0A] bg-white p-6" data-testid={`quote-${i}`}>
              <Quote size={20} className="text-[#FF4F00]" />
              <blockquote className="mt-3 text-sm sm:text-base leading-relaxed">"{q.quote}"</blockquote>
              <figcaption className="mt-5 pt-4 border-t border-[#DEE2E6]">
                <div className="font-heading font-semibold tracking-tight">{q.who}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#495057] mt-0.5">{q.role}</div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───── Final CTA ───── */
function FinalCTA({ onTryLive, demoLoading, onContact }) {
  return (
    <section id="demo" className="bg-[#FF4F00] text-white py-16 sm:py-20" data-testid="final-cta">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
        <div>
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.25em] text-white/80 font-mono">Ready to look around?</div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter mt-2 leading-tight">
            Skip the slides. <br />Click into a real Gr8Boost workspace.
          </h2>
          <p className="text-white/80 mt-3 text-sm sm:text-base">A fully-seeded sandbox spins up in 3 seconds. No signup. No credit card. Yours to explore for 24 hours.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 lg:shrink-0">
          <button onClick={onTryLive} disabled={demoLoading} data-testid="cta-try-live" className="inline-flex items-center justify-center gap-2 bg-white text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white px-6 py-3 text-sm font-medium disabled:opacity-60">
            {demoLoading ? <><Loader2 size={14} className="animate-spin" /> Loading…</> : <>Try it live <ArrowRight size={14} /></>}
          </button>
          <button onClick={onContact} data-testid="cta-email" className="inline-flex items-center justify-center gap-2 border-2 border-white hover:bg-white hover:text-[#0A0A0A] px-6 py-3 text-sm font-medium">
            Contact sales
          </button>
        </div>
      </div>
    </section>
  );
}

/* ───── Footer ───── */
function Footer({ onContact }) {
  return (
    <footer className="bg-[#0A0A0A] text-white py-12 sm:py-14" data-testid="footer">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-4 gap-8">
        <div>
          <Logo size={28} dark="#FFFFFF" />
          <p className="text-xs text-white/60 mt-3 leading-relaxed max-w-xs">The white-label ERP, powered by Gr8Agentic AI. Built for ambitious businesses worldwide.</p>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-3">Product</div>
          <ul className="space-y-2 text-sm">
            <li><a href="#modules" className="hover:text-[#FF4F00]">Modules</a></li>
            <li><a href="#copilot" className="hover:text-[#FF4F00]">Gr8Agentic AI</a></li>
            <li><a href="#pricing" className="hover:text-[#FF4F00]">Pricing</a></li>
            <li><Link to="/roadmap" className="hover:text-[#FF4F00]">Public roadmap</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-3">Company</div>
          <ul className="space-y-2 text-sm">
            <li><a href="#customers" className="hover:text-[#FF4F00]">Customers</a></li>
            <li><button onClick={onContact} data-testid="footer-contact-sales" className="hover:text-[#FF4F00] text-left">Contact sales</button></li>
            <li><Link to="/login" className="hover:text-[#FF4F00]">Sign in</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-3">Compliance</div>
          <ul className="space-y-2 text-sm text-white/70">
            <li className="inline-flex items-center gap-1.5"><ShieldCheck size={12} className="text-[#0B7A75]" />SOC-2-ready architecture</li>
            <li className="inline-flex items-center gap-1.5"><Check size={12} className="text-[#0B7A75]" />Multi-region tax engines (VAT, GST, sales tax)</li>
            <li className="inline-flex items-center gap-1.5"><Check size={12} className="text-[#0B7A75]" />PayPal · Stripe · MTN MoMo billing</li>
          </ul>
        </div>
      </div>
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 mt-10 pt-6 border-t border-white/10 flex flex-wrap items-center justify-between gap-3 text-[10px] uppercase tracking-[0.2em] text-white/40">
        <div>© {new Date().getFullYear()} Gr8Boost ERP · A global white-label ERP platform</div>
        <div className="flex flex-wrap gap-x-5 gap-y-2">
          <Link to="/privacy" className="hover:text-white" data-testid="footer-privacy">Privacy</Link>
          <Link to="/terms" className="hover:text-white" data-testid="footer-terms">Terms</Link>
          <span>Powered by Gr8Agentic AI</span>
        </div>
      </div>
    </footer>
  );
}
